import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { UseFormReturn } from 'react-hook-form';
import { getDeployTarget } from '../../../../utils/url-utils';
import { regex } from '../../../../utils/validation';
import type { CustomersValidateTrackOrderRequest } from '../../../../bff-api/customers/model';
import type { FieldConfig } from '../../../../components/field';

const messages = defineMessages({
    required: { defaultMessage: '{field} is required', id: 'error.field.required' },
    invalidFormat: { defaultMessage: 'Enter a valid {field}', id: 'error.field.invalid' },
    orderNumber: {
        defaultMessage: 'Order Number',
        id: 'account.track-order.order_number',
    },
    email: {
        defaultMessage: 'Order Email',
        id: 'account.track-order.email',
    },
});

export function useTrackOrderFields(
    form: UseFormReturn<CustomersValidateTrackOrderRequest>,
): Record<string, FieldConfig> {
    const {
        control,
        formState: { errors },
    } = form;
    const intl = useIntl();
    const orderNoPattern = useMemo(() => {
        switch (getDeployTarget()) {
            case 'production':
                return /^SF\d{10}$/;
            case 'staging':
                return /^SFT\d{10}$/;
            case 'development':
                return /^SL-DEV\d{10}$/;
            default:
                return /^\d{8}$/;
        }
    }, []);

    const fields: Record<string, FieldConfig> = {
        orderNo: {
            id: 'account-track-order-order-number-field',
            name: 'orderNo',
            label: intl.formatMessage(messages.orderNumber),
            defaultValue: '',
            type: 'text',
            rules: {
                required: intl.formatMessage(messages.required, { field: 'Order number' }),
                pattern: {
                    value: orderNoPattern,
                    message: intl.formatMessage(messages.invalidFormat, { field: 'order number' }),
                },
            },
            error: errors.orderNo,
            control,
            variant: 'new-style',
        },
        email: {
            id: 'account-track-order-email-field',
            name: 'email',
            label: intl.formatMessage(messages.email),
            type: 'email',
            defaultValue: '',
            rules: {
                required: intl.formatMessage(messages.required, { field: 'Email address' }),
                pattern: {
                    value: regex.email,
                    message: intl.formatMessage(messages.invalidFormat, { field: 'email address' }),
                },
            },
            error: errors.email,
            control,
            variant: 'new-style',
        },
    };

    return fields;
}
